import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, RefreshCw, Unplug } from 'lucide-react';
import type { ChannelAccountInfo, ChannelInfo, ConnectResponse, StoreRow } from '@berytus/contracts';
import { api, dateTime, int } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { useMe } from '../components/AuthGate.tsx';
import { can } from '@berytus/contracts';

const LOGO: Record<string, { text: string; bg: string }> = {
  mercadolivre: { text: 'ML', bg: 'var(--ch-mercadolivre)' },
  shopee: { text: 'S', bg: 'var(--ch-shopee)' },
  ifood: { text: 'iF', bg: 'var(--ch-ifood)' },
  '99food': { text: '99', bg: 'var(--ch-99food)' },
};

const ACCOUNT_STATUS: Record<ChannelAccountInfo['status'], { label: string; tone: string }> = {
  active: { label: 'Conectada', tone: 'ok' },
  expired: { label: 'Precisa reconectar', tone: 'err' },
  error: { label: 'Com erro', tone: 'err' },
  pending: { label: 'Pendente', tone: 'warn' },
  revoked: { label: 'Desconectada', tone: 'off' },
};

const PULL_STATUS: Record<string, { label: string; tone: string }> = {
  queued: { label: 'Na fila', tone: 'info' },
  running: { label: 'Lendo anúncios…', tone: 'info' },
  succeeded: { label: 'Concluída', tone: 'ok' },
  partial: { label: 'Parcial', tone: 'warn' },
  failed: { label: 'Falhou', tone: 'err' },
  cancelled: { label: 'Cancelada', tone: 'off' },
};

const pulling = (a: ChannelAccountInfo) => a.pull?.status === 'queued' || a.pull?.status === 'running';

export function Channels() {
  const q = useQuery({
    queryKey: ['channels'],
    queryFn: () => api<ChannelInfo[]>('/channels'),
    // Enquanto uma leitura está em andamento, atualiza sozinho.
    refetchInterval: (query) => (query.state.data?.some((c) => c.accounts.some(pulling)) ? 3000 : false),
  });

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Integração de canais</h1>
          <p>Conecte as contas da loja em cada canal. Por enquanto a Berytus só lê: nada é alterado nos anúncios.</p>
        </div>
      </div>
      {q.isPending ? <Loading /> : q.error ? <ErrorBox error={q.error} /> : (
        <div className="ch-cards">
          {q.data.map((c) => <ChannelCard key={c.code} c={c} />)}
        </div>
      )}
    </>
  );
}

function ChannelCard({ c }: { c: ChannelInfo }) {
  const me = useMe();
  const admin = can(me.tenant.access, 'channel_accounts', 'write');
  const accounts = c.accounts.filter((a) => a.status !== 'revoked');
  const logo = LOGO[c.code]!;

  return (
    <section className="panel ch-card">
      <div className="ch-head">
        <span className="ch-logo" style={{ background: logo.bg }} aria-hidden>{logo.text}</span>
        <div>
          <h2>{c.name}</h2>
          <span className="small muted">{c.kind === 'marketplace' ? 'Marketplace' : 'Delivery (uma conta por loja)'}</span>
        </div>
        <span style={{ flex: 1 }} />
        {!c.available && <Tag tone="off">em breve</Tag>}
        {c.environment === 'test' && <Tag tone="warn">ambiente de teste</Tag>}
      </div>

      {c.available && !c.configured && (
        <div className="notice warn">
          {c.configError && <><b>{c.configError}</b><br /></>}
          As credenciais do app não estão no servidor. Preencha{' '}
          {c.code === 'shopee'
            ? <><span className="code">SHOPEE_PARTNER_ID</span>, <span className="code">SHOPEE_PARTNER_KEY</span></>
            : c.code === 'ifood' ? <><span className="code">IFOOD_CLIENT_ID</span>, <span className="code">IFOOD_CLIENT_SECRET</span></>
            : <><span className="code">MELI_CLIENT_ID</span>, <span className="code">MELI_CLIENT_SECRET</span>, <span className="code">MELI_REDIRECT_URI</span></>}
          {' '}e <span className="code">TOKEN_ENCRYPTION_KEY</span> no <span className="code">.env</span> e reinicie a API.
        </div>
      )}

      {accounts.map((a) => <Account key={a.id} a={a} admin={admin} channel={c.code} />)}

      {c.available && c.configured && admin && c.code === 'ifood' && <IfoodConnect />}
      {c.available && c.configured && admin && c.code !== 'ifood' && (c.code === 'shopee' || accounts.length === 0 || accounts.some((a) => a.status !== 'active')) && (
        <Connect again={c.code !== 'shopee' && accounts.length > 0} channel={c.code} name={c.name} />
      )}

      {!c.available && (
        <p className="small muted" style={{ margin: 0 }}>
          A conexão com {c.name} vem depois do Mercado Livre, com a mesma tela de produtos × anúncios.
        </p>
      )}
    </section>
  );
}

function Account({ a, admin, channel }: { a: ChannelAccountInfo; admin: boolean; channel: string }) {
  const meli = channel === 'mercadolivre';
  const qc = useQueryClient();
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['channels'] });
    qc.invalidateQueries({ queryKey: ['meli'] });
  };
  const pull = useMutation({ mutationFn: () => api(`/channels/accounts/${a.id}/pull`, { method: 'POST' }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: () => api(`/channels/accounts/${a.id}`, { method: 'DELETE' }), onSuccess: refresh });
  const st = ACCOUNT_STATUS[a.status];
  const p = a.pull;
  const items = typeof p?.stats?.items === 'number' ? (p.stats.items as number) : null;

  return (
    <div className="acct">
      <div className="acct-top">
        <b>{a.displayName ?? a.externalAccountId}</b>
        <Tag tone={st.tone}>{st.label}</Tag>
      </div>
      <span className="small muted">{meli ? 'Vendedor' : 'Loja'} nº {a.externalAccountId} · conectada em {dateTime(a.createdAt)}</span>
      <StorePick a={a} admin={admin} />
      {a.lastError && <div className="notice err small">{a.lastError}</div>}
      {p && (
        <div className="small" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          Última leitura: <Tag tone={PULL_STATUS[p.status]?.tone ?? 'off'}>{PULL_STATUS[p.status]?.label ?? p.status}</Tag>
          {items != null && <span className="muted">{int(items)} anúncios</span>}
          {p.finishedAt && <span className="muted">{dateTime(p.finishedAt)}</span>}
          {p.status === 'failed' && p.error && <span style={{ color: 'var(--err)' }}>{p.error}</span>}
        </div>
      )}
      {pull.error && <ErrorBox error={pull.error} />}
      {remove.error && <ErrorBox error={remove.error} />}
      <div className="actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
        {a.status === 'active' && channel === 'ifood' && <Link className="btn sm primary" to="/canal/ifood">Abrir canal</Link>}
        {a.status === 'active' && meli && (
          <>
            <Link className="btn sm primary" to="/canal/mercadolivre">Abrir canal</Link>
            <button className="btn sm" type="button" disabled={pulling(a) || pull.isPending} onClick={() => pull.mutate()}>
              <RefreshCw size={14} /> {pulling(a) ? 'Lendo…' : 'Ler anúncios agora'}
            </button>
          </>
        )}
        {admin && (
          <button
            className="btn sm quiet"
            type="button"
            disabled={remove.isPending}
            onClick={() => {
              if (confirm('Desconectar esta conta? Os anúncios lidos continuam no histórico; para voltar, é preciso autorizar de novo.')) remove.mutate();
            }}
          >
            <Unplug size={14} /> Desconectar
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Conexão: abre a autorização do Mercado Livre numa aba nova. Com berytus.com.br no ar, o retorno
 * conclui sozinho (/app/meli/). Antes disso, o usuário cola aqui o endereço da página de retorno.
 */
function Connect({ again, channel, name }: { again: boolean; channel: string; name: string }) {
  const qc = useQueryClient();
  const [redirect, setRedirect] = useState<string | null>(null);
  const [pasted, setPasted] = useState('');

  const start = useMutation({
    mutationFn: async () => {
      // A aba é aberta já no clique (senão o navegador bloqueia) e recebe o endereço quando a API responde.
      const tab = window.open('about:blank', '_blank');
      if (tab) tab.opener = null; // a página do canal não ganha acesso a esta aba
      try {
        const r = await api<ConnectResponse>(`/channels/${channel}/connect`, { method: 'POST' });
        if (tab) tab.location.href = r.url;
        else window.location.href = r.url;
        return r;
      } catch (err) {
        tab?.close();
        throw err;
      }
    },
    onSuccess: (r) => setRedirect(r.redirectUri),
  });

  const finish = useMutation({
    mutationFn: (url: string) => api<{ nickname: string }>(`/channels/${channel}/callback`, { method: 'POST', json: { url } }),
    onSuccess: () => {
      setPasted('');
      setRedirect(null);
      qc.invalidateQueries({ queryKey: ['channels'] });
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (pasted.trim()) finish.mutate(pasted.trim());
  };

  return (
    <div className="stack-y" style={{ gap: 12 }}>
      <button className="btn primary" type="button" onClick={() => start.mutate()} disabled={start.isPending}>
        <ExternalLink size={16} /> {again ? `Reconectar ${name}` : channel === 'shopee' ? 'Conectar loja da Shopee' : `Conectar ${name}`}
      </button>
      {start.error && <ErrorBox error={start.error} />}
      {redirect && (
        <form className="notice" onSubmit={submit} style={{ display: 'grid', gap: 10 }}>
          <ol className="steps">
            <li>Na aba que abriu, entre com a conta da loja {channel === 'shopee' ? 'na Shopee' : 'no Mercado Livre'} e autorize a Berytus.</li>
            <li>
              {channel === 'shopee' ? 'A Shopee' : 'O Mercado Livre'} leva para <span className="code">{redirect}</span>. Se essa página concluir sozinha, pronto.
            </li>
            <li>Se não abrir (o site ainda não está no ar), copie o endereço inteiro da barra do navegador e cole aqui:</li>
          </ol>
          <input
            className="input"
            placeholder={channel === 'shopee' ? `${redirect}?state=…&code=…&shop_id=…` : `${redirect}?code=TG-…&state=…`}
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
            aria-label={`Endereço de retorno ${channel === 'shopee' ? 'da Shopee' : 'do Mercado Livre'}`}
          />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn primary sm" type="submit" disabled={!pasted.trim() || finish.isPending}>
              {finish.isPending ? 'Conectando…' : 'Concluir conexão'}
            </button>
            <span className="small muted">O código vale 10 minutos.</span>
          </div>
          {finish.error && <ErrorBox error={finish.error} />}
        </form>
      )}
    </div>
  );
}

/**
 * iFood (app distribuído): a Berytus pede um código ao iFood; o lojista abre o Portal do Parceiro, informa o código,
 * autoriza e recebe um código de autorização, que cola aqui. Cada loja autorizada vira uma conexão.
 */
function IfoodConnect() {
  const qc = useQueryClient();
  const [auth, setAuth] = useState('');
  const start = useMutation({
    mutationFn: () => api<{ state: string; userCode: string; verificationUrl: string; verificationUrlComplete: string; expiresIn: number }>('/channels/ifood/connect', { method: 'POST' }),
  });
  const finish = useMutation({
    mutationFn: () => api<{ accounts: { id: string; name: string }[] }>('/channels/ifood/callback', { method: 'POST', json: { state: start.data!.state, authorizationCode: auth } }),
    onSuccess: () => { setAuth(''); start.reset(); qc.invalidateQueries({ queryKey: ['channels'] }); },
  });
  return (
    <div className="stack-y" style={{ gap: 12 }}>
      {!start.data ? (
        <button className="btn primary" type="button" onClick={() => start.mutate()} disabled={start.isPending}>
          <ExternalLink size={16} /> {start.isPending ? 'Pedindo código…' : 'Conectar loja do iFood'}
        </button>
      ) : (
        <form className="notice" style={{ display: 'grid', gap: 10 }} onSubmit={(e) => { e.preventDefault(); if (auth.trim()) finish.mutate(); }}>
          <ol className="steps">
            <li>
              Abra o <a href={start.data.verificationUrlComplete} target="_blank" rel="noreferrer">Portal do Parceiro do iFood <ExternalLink size={12} /></a>{' '}
              e entre com a conta da loja. Se pedir, informe o código <b className="code" style={{ fontSize: 16, letterSpacing: '.08em' }}>{start.data.userCode}</b>.
            </li>
            <li>Autorize a Berytus para a(s) loja(s). O portal mostra um <b>código de autorização</b>.</li>
            <li>Cole aqui o código de autorização:</li>
          </ol>
          <input className="input" autoFocus placeholder="Código de autorização" value={auth} onChange={(e) => setAuth(e.target.value)} aria-label="Código de autorização do iFood" />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn primary sm" type="submit" disabled={!auth.trim() || finish.isPending}>{finish.isPending ? 'Conectando…' : 'Concluir conexão'}</button>
            <button className="btn sm quiet" type="button" onClick={() => { start.reset(); finish.reset(); setAuth(''); }}>Cancelar</button>
            <span className="small muted">O código vale {Math.round((start.data.expiresIn || 600) / 60)} minutos.</span>
          </div>
          {finish.error && <ErrorBox error={finish.error} />}
        </form>
      )}
      {start.error && <ErrorBox error={start.error} />}
      {finish.data && <div className="notice small">Conectada(s): {finish.data.accounts.map((a) => a.name).join(', ')}. Escolha abaixo de qual loja (estoque) cada uma vende.</div>}
    </div>
  );
}

/** Loja (mesmo CNPJ) cujo estoque esta conexão vende. */
function StorePick({ a, admin }: { a: ChannelAccountInfo; admin: boolean }) {
  const qc = useQueryClient();
  const stores = useQuery({ queryKey: ['stores'], queryFn: () => api<StoreRow[]>('/stores'), enabled: admin });
  const set = useMutation({
    mutationFn: (storeId: string) => api(`/channels/accounts/${a.id}/store`, { method: 'PUT', json: { storeId } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['channels'] }); qc.invalidateQueries({ queryKey: ['stores'] }); qc.invalidateQueries({ queryKey: ['meli'] }); },
  });
  const active = stores.data?.filter((s) => s.active) ?? [];
  return (
    <div className="small" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      Vende o estoque de:
      {admin && active.length > 1 ? (
        <select className="select" style={{ height: 30, width: 'auto' }} value={a.store?.id ?? ''} disabled={set.isPending}
          onChange={(e) => e.target.value && confirm('Trocar a loja? O estoque enviado a este canal passa a ser o da loja escolhida.') && set.mutate(e.target.value)}>
          {!a.store && <option value="">—</option>}
          {active.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      ) : <b>{a.store?.name ?? 'Loja principal'}</b>}
      {admin && active.length <= 1 && <Link to="/configuracoes/lojas" className="muted">cadastrar outra loja</Link>}
      {set.error && <ErrorBox error={set.error} />}
    </div>
  );
}
